import type { Event, Question, Section, Participant, SurveySession, ResponseRow } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || '';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || `HTTP ${res.status}`);
  }
  return res.json();
}

export interface EventSummary {
  id: number;
  name: string;
  slug: string;
  event_date: string;
}

export async function getEvents(): Promise<EventSummary[]> {
  const json = await request<{ success: boolean; data: EventSummary[] }>('/api/events/list');
  if (!json.success) throw new Error('Failed to load events');
  return json.data;
}

export async function getEvent(idOrSlug: string): Promise<Event> {
  const json = await request<{ success: boolean; data: Event }>(`/api/events/${encodeURIComponent(idOrSlug)}`);
  if (!json.success) throw new Error('Event not found');
  return json.data;
}

export interface EventWithQuestions {
  event: Event;
  sections: Section[];
  questions: Question[];
}

export async function getEventQuestions(idOrSlug: string): Promise<EventWithQuestions> {
  const json = await request<{ success: boolean; data: EventWithQuestions }>(
    `/api/events/${encodeURIComponent(idOrSlug)}/questions`
  );
  if (!json.success) throw new Error('Event not found');
  return json.data;
}

export async function ensureParticipant(cookieId?: string): Promise<{ participant: Participant; cookieId: string }> {
  const body = cookieId ? { cookieId } : {};
  const json = await request<{ success: boolean; data: { participant: Participant; cookieId: string } }>(
    '/api/participants/ensure',
    { method: 'POST', body: JSON.stringify(body) }
  );
  if (!json.success) throw new Error('Failed to ensure participant');
  return json.data;
}

export async function getSession(participantId: number, eventId: number): Promise<SurveySession | null> {
  const json = await request<{ success: boolean; data: SurveySession | null }>(
    `/api/sessions/${participantId}/${eventId}`
  );
  if (!json.success) throw new Error('Failed to get session');
  return json.data;
}

export async function ensureSession(participantId: number, eventId: number): Promise<SurveySession> {
  const json = await request<{ success: boolean; data: SurveySession }>('/api/sessions/ensure', {
    method: 'POST',
    body: JSON.stringify({ participantId, eventId }),
  });
  if (!json.success) throw new Error('Failed to ensure session');
  return json.data;
}

export async function updateSession(
  participantId: number,
  eventId: number,
  updates: { lastQuestionId?: number | null; completedAt?: string | null; reset?: boolean }
): Promise<SurveySession> {
  const body: Record<string, unknown> = {};
  if (updates.lastQuestionId !== undefined) body.lastQuestionId = updates.lastQuestionId;
  if (updates.completedAt !== undefined) body.completedAt = updates.completedAt;
  if (updates.reset === true) body.reset = true;
  const json = await request<{ success: boolean; data: SurveySession }>(
    `/api/sessions/${participantId}/${eventId}`,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
  if (!json.success) throw new Error('Failed to update session');
  return json.data;
}

export async function saveResponse(
  participantId: number,
  eventId: number,
  questionId: number,
  payload: { optionId?: number; numericValue?: number; textValue?: string }
): Promise<void> {
  await request('/api/responses', {
    method: 'POST',
    body: JSON.stringify({
      participantId,
      eventId,
      questionId,
      ...payload,
    }),
  });
}

export async function saveResponsesBulk(
  participantId: number,
  eventId: number,
  responses: Array<{ questionId: number; optionId?: number; numericValue?: number; textValue?: string }>
): Promise<void> {
  await request('/api/responses/bulk', {
    method: 'POST',
    body: JSON.stringify({ participantId, eventId, responses }),
  });
}

export async function getResponses(participantId: number, eventId: number): Promise<ResponseRow[]> {
  const json = await request<{ success: boolean; data: ResponseRow[] }>(
    `/api/responses/${participantId}/${eventId}`
  );
  if (!json.success) throw new Error('Failed to load responses');
  return json.data;
}

export async function deleteResponses(participantId: number, eventId: number): Promise<void> {
  await request(`/api/responses/${participantId}/${eventId}`, { method: 'DELETE' });
}

export async function deleteResponsesForQuestion(
  participantId: number,
  eventId: number,
  questionId: number
): Promise<void> {
  await request(
    `/api/responses/${participantId}/${eventId}/question/${questionId}`,
    { method: 'DELETE' }
  );
}
